import {
  Alert,
  Button,
  LinkButton,
  RelativeTime,
  Sidebar,
  Skeleton,
  Stack,
  ToggleButton,
  Typography,
} from '@compass/components'
import { cn } from '@compass/utils'
import Link from '@mui/material/Link'
import { Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Link as RouterLink } from '@tanstack/react-router'
import { AppRoute, buildRoute } from 'appRoutes'
import { formatDatetime, MainColumn, SetHeader } from 'features/commonUI'
import { useCurrentSubscription } from 'features/subscription'
import { formatJSON } from 'helpers/format'
import { MIMEType } from 'helpers/types'
import { useCurrentUser } from 'hooks/api/users'
import { useVisitorEventsCountV4, VISITOR_EVENTS_COUNT_CAP } from 'hooks/events'
import {
  AlertTriangleIcon,
  ChevronLeftIcon,
  FileJson2Icon,
  HistoryIcon,
  PanelRightOpenIcon,
  Search,
  ShieldCheckIcon,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { DateRangePeriod } from 'models'
import { ampli } from 'models/ampli'
import { BotResult, IdEventV3, IdEventV4 } from 'models/event'
import { ReactNode, useMemo, useState } from 'react'

import { useServerApiVersion } from '../../hooks/useServerApiVersion'
import { visitFilterLabelMap } from '../../utils/const'
import { CodeViewerModal } from './CodeViewerModal'
import { detectedSmartSignals } from './definitions'
import { ServerApiVersionSelector } from './ServerApiVersionSelector'
import { SidebarContent } from './SidebarContent'
import { usePersistedEventFilterSearch } from './usePersistedEventFilterParams'

export function EventLayout({
  event,
  eventJson,
  requestId,
  error,
  children,
}: {
  requestId: string
  event?: IdEventV4
  eventJson?: IdEventV3 | IdEventV4
  error?: Error | null
  children?: ReactNode
}) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const { data: currentUser } = useCurrentUser()
  const { serverApiVersion } = useServerApiVersion()
  const identification = event?.identification
  const visitorId = identification?.visitor_id

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const search = usePersistedEventFilterSearch()

  // Fixed “now − 7 days … now” window captured on mount, shared by the visits counter and the events link
  // so both cover the exact same range (including today).
  const dateRange = useMemo(() => {
    const now = DateTime.now().startOf('minute')
    return { since: now.minus({ days: 7 }), before: now }
  }, [])

  const visitsCountLabel = useVisitsCountLabel(subscriptionId, visitorId, dateRange)

  const visitsHistoryHref = visitorId
    ? buildRoute(
        AppRoute.IdentificationEvents,
        { subscriptionId },
        { visitorId, since: dateRange.since.toISO()!, before: dateRange.before.toISO()! }
      )
    : undefined

  const navigationBar = (
    <Stack className='w-full'>
      <LinkButton
        href={buildRoute(AppRoute.IdentificationEvents, { subscriptionId }) + (search ?? '')}
        variant='ghost'
        tone='background2'
        size='sm'
        alt
        className='-ml-3'
      >
        <ChevronLeftIcon /> Back to events
      </LinkButton>
      <div className='flex gap-2 ml-auto'>
        {visitorId && visitsCountLabel !== null ? (
          <>
            {/* Large screens */}
            <LinkButton
              variant='secondary'
              size='sm'
              href={visitsHistoryHref}
              onPress={() => ampli.identificationVisitorHistoryExplorationChosen()}
              className='hidden sm:inline-flex animate-in fade-in'
            >
              <HistoryIcon />
              Visitor history
              <span className='text-gray-800 font-normal'>{visitsCountLabel}</span>
            </LinkButton>
            {/* Small screens */}
            <LinkButton
              aria-label='Visitor history'
              variant='secondary'
              size='sm'
              isIcon
              href={visitsHistoryHref}
              onPress={() => ampli.identificationVisitorHistoryExplorationChosen()}
              className='sm:hidden animate-in fade-in'
            >
              <HistoryIcon />
            </LinkButton>
          </>
        ) : null}
        <CodeViewerModal
          value={formatJSON((eventJson as IdEventV3)?.products ?? eventJson ?? {})}
          fileName='response.json'
          searchAction={<ServerApiVersionSelector />}
          mimeType={MIMEType.PlainText}
          onCopy={() => ampli.identificationRequestCopied()}
        >
          <Button
            variant='secondary'
            size='sm'
            isDisabled={eventJson == null}
            onPress={() => {
              ampli.identificationEventDetailsJsonViewed({ tabName: 'details', ApiVersionJson: serverApiVersion })
            }}
          >
            <FileJson2Icon /> Show JSON
          </Button>
        </CodeViewerModal>
        <ToggleButton
          aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          variant='ghost'
          tone='background2'
          size='sm'
          isIcon
          isSelected={sidebarOpen}
          onPress={() => setSidebarOpen((o) => !o)}
          className='lg:hidden'
        >
          <PanelRightOpenIcon />
        </ToggleButton>
      </div>
    </Stack>
  )

  const header = (
    <Stack direction='column' gap={2}>
      <Stack
        direction={{ default: 'column', sm: 'row' }}
        gapX={4}
        gapY={2}
        className='items-start sm:items-center flex-wrap'
      >
        <span className='font-mono text-2xl font-medium'>{requestId}</span>
        <Typography size='sm' className='text-gray-800'>
          {event ? (
            <RelativeTime date={DateTime.fromMillis(event.timestamp)}>
              {formatDatetime(
                DateTime.fromMillis(event.timestamp).toJSDate(),
                'standard',
                currentUser?.timezone ?? 'UTC',
                'shortWithYear'
              )}
            </RelativeTime>
          ) : (
            <Skeleton className='w-32 h-4' />
          )}
        </Typography>
      </Stack>
      <Detections event={event} subscriptionId={subscriptionId} />
    </Stack>
  )

  return (
    <>
      <SetHeader title='Identification' />
      <MainColumn className='flex-row md:gap-6 py-0 lg:pr-0'>
        <main className='flex flex-col gap-6 md:gap-8 w-full py-4'>
          <Stack direction='column' gap={4} className='items-start'>
            {navigationBar}
            {header}
          </Stack>

          {error ? (
            <Alert variant='error'>{error.message ?? 'There was an error getting the details for this visit.'}</Alert>
          ) : (
            children
          )}
        </main>
        <Aside mobileOpen={sidebarOpen} event={event} onClose={() => setSidebarOpen(false)} />
      </MainColumn>
    </>
  )
}

/**
 * Display label for the number of events for this visitor over the given date range, fetched via
 * the v4 search endpoint as a count only (no event rows). Counts above the cap render as
 * "{cap}+". Returns null while loading.
 */
function useVisitsCountLabel(
  subscriptionId: string,
  visitorId: string | undefined,
  dateRange: { since: DateTime; before: DateTime }
) {
  const {
    data: totalHits,
    isError,
    isLoading,
  } = useVisitorEventsCountV4({ subscriptionId, visitorId, ...dateRange }, visitorId != null)

  if (isLoading) {
    return null
  }

  if (isError) {
    return '—'
  }

  if (totalHits === undefined || totalHits <= 1) {
    return null
  }

  const displayTotal = Math.min(totalHits, VISITOR_EVENTS_COUNT_CAP)
  return `${displayTotal.toLocaleString()}${totalHits > VISITOR_EVENTS_COUNT_CAP ? '+' : ''}`
}

const displayCount = 3

function Detections({ event, subscriptionId }: { event?: IdEventV4; subscriptionId: string }) {
  const detected = detectedSmartSignals(event)

  if (event == null) {
    return <Skeleton className='w-45 h-4 mt-1' data-testid='detected-signals-loader' />
  }

  if (detected.length === 0) {
    return (
      <div className='flex gap-2 items-center'>
        <ShieldCheckIcon size={14} className='text-gray-800' />
        <Typography size='sm' className='text-gray-800'>
          No Smart Signals detected
        </Typography>
      </div>
    )
  }

  return (
    <Stack direction='row' gap={2} className='items-center flex-wrap'>
      {detected.slice(0, displayCount).map((d, index) => (
        <Stack key={d.key} direction='row' gap={2} className='items-center flex-nowrap'>
          <Stack direction='row' gap={1} className='items-center flex-nowrap'>
            {typeof d.icon === 'function' ? d.icon(event) : d.icon}
            <Typography
              size='sm'
              weight='medium'
              className={(typeof d.className === 'function' ? d.className(event) : d.className) ?? 'text-red-800'}
            >
              {d.label}
            </Typography>
          </Stack>
          {detected.length > displayCount || index !== detected.length - 1 ? <span className='border-r h-4' /> : null}
        </Stack>
      ))}
      {detected.length > 3 ? (
        <Link
          component={RouterLink}
          to={buildRoute(AppRoute.IdentificationEventDetails, {
            subscriptionId,
            requestId: event.event_id,
          })}
          hash='scroll-smart-signals'
          underline='none'
          className='flex gap-2 items-center text-nowrap'
        >
          <AlertTriangleIcon size={16} className='text-gray-800' />
          <Typography
            size='sm'
            weight='medium'
            className='text-gray-800 underline underline-offset-2 decoration-1 decoration-dotted'
          >
            +{detected.length - 3} detected
          </Typography>
        </Link>
      ) : null}
      {detected.some((d) => 'urlParamKey' in d) && (
        <RouterLink
          className='flex gap-2 items-center text-nowrap'
          to='/workspaces/$subscriptionId/events'
          params={{ subscriptionId }}
          search={reduceDetectionsToUrlParams(detected, event)}
          onClick={() => {
            ampli.attributeFilterApplied({
              datasetFocus: 'Identification Events',
              attributeFilters: detected
                .map((d) => (d.urlParamKey ? visitFilterLabelMap[d.urlParamKey] : undefined))
                .filter(Boolean) as string[],
              filterType: 'findSimilarEvents',
            })
          }}
        >
          <span className='border-l h-4' />
          <Search size={16} className='text-gray-800' />{' '}
          <Typography
            size='sm'
            weight='medium'
            className='text-gray-800 underline underline-offset-2 decoration-1 decoration-dotted'
          >
            Find similar events
          </Typography>
        </RouterLink>
      )}
    </Stack>
  )
}

function Aside({ mobileOpen, onClose, event }: { mobileOpen?: boolean; onClose?: () => void; event?: IdEventV4 }) {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'))

  return (
    <Sidebar
      position='right'
      variant={isDesktop ? 'persistent' : 'floating'}
      isOpen={mobileOpen || isDesktop}
      onOpenChange={(open: boolean) => {
        if (!open) {
          onClose?.()
        }
      }}
      className={cn('shrink-0', 'w-64 h-svh', 'bg-gray-100', 'border-l', 'overscroll-auto')}
      data-testid='event-detail-sidebar'
    >
      <SidebarContent event={event} className='sticky top-[60px] px-6 py-4' />
    </Sidebar>
  )
}

function reduceDetectionsToUrlParams(detected: ReturnType<typeof detectedSmartSignals>, event: IdEventV4) {
  return detected.reduce<{ period: DateRangePeriod['period'] } & Record<string, unknown>>(
    (acc, d) => {
      if (!d.urlParamKey) {
        return acc
      }

      if (d.key === 'bot-detection') {
        acc[d.urlParamKey] = (d.extract(event) as { bot?: BotResult })?.bot
        return acc
      }

      acc[d.urlParamKey] = d.isDetected(event)
      return acc
    },
    { period: 'last_7_days' }
  )
}
